/**
 * 🍓 utils-urls.js - Safe URL Encoding & Decoding Utility (ES Module Version)
 *
 * 🔹 Provides safe encoding and decoding for URLs
 * 🔹 Avoids double encoding issues
 * 🔹 Handles errors gracefully
 *
 * 📝 Usage Example:
 *
 * import { encodePath, decodePath } from "./utils-urls.js";
 * console.log("Decoded path:", decodePath(window.location.pathname));
 *
 * let encodedPath = encodePath("/zh/カテゴリ");
 * window.history.pushState({}, "", encodedPath);
 */

console.log('✅ utils-urls.js loaded successfully!'); // Debugging confirmation

/**
 * ✅ Safely decodes a URI component
 * @param {string} urlSegment - The encoded URI component
 * @returns {string} - The decoded string (or original if decoding fails)
 */
export function decode(urlSegment) {
  try {
    return decodeURIComponent(urlSegment);
  } catch (e) {
    console.error('❌ Error decoding URI component:', e);
    return urlSegment; // Return original if decoding fails
  }
}

/**
 * ✅ Safely encodes a URI component
 * @param {string} urlSegment - The URI component to encode
 * @returns {string} - The safely encoded string
 */
export function encode(urlSegment) {
  try {
    return encodeURIComponent(urlSegment);
  } catch (e) {
    console.error('❌ Error encoding URI component:', e);
    return urlSegment; // Return original if encoding fails
  }
}

/**
 * ✅ Safely decodes a full URL path
 * @param {string} path - The full URL path (e.g., window.location.pathname)
 * @returns {string} - The safely decoded path
 */
export function decodePath(path) {
  try {
    return decodeURIComponent(decodeURI(path)); // Double decode for safety
  } catch (e) {
    console.error('❌ Error decoding URL path:', e);
    return path; // Return original if decoding fails
  }
}

/**
 * ✅ Safely encodes a full URL path
 * @param {string} path - The full URL path
 * @returns {string} - The safely encoded path
 */
export function encodePath(path) {
  try {
    return encodeURI(path);
  } catch (e) {
    console.error('❌ Error encoding URL path:', e);
    return path; // Return original if encoding fails
  }
}

/**
 * ✅ Checks if a string is already percent-encoded
 * @param {string} str - The string to check
 * @returns {boolean} - True if encoded, False if not
 */
export function isEncoded(str) {
  try {
    return str !== decodeURIComponent(str);
  } catch (e) {
    return false; // If decoding fails, it's likely not encoded
  }
}

/**
 * ✅ Safely encodes a string only if it is NOT already encoded
 * @param {string} str - The string to encode
 * @returns {string} - The encoded string (or original if already encoded)
 */
export function safeEncodeURI(str) {
  return isEncoded(str) ? str : encodeURIComponent(str);
}

/**
 * ✅ Safely decodes a string only if it is encoded
 * @param {string} str - The string to decode
 * @returns {string} - The decoded string (or original if not encoded)
 */
export function safeDecodeURI(str) {
  return isEncoded(str) ? decodeURIComponent(str) : str;
}
